import React from 'react';
import { ArrowRight, Clock, MapPin, CheckCircle } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { transformationProject } from '../data/mock';

const TransformationShowcase = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 via-black to-gray-900 relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="bg-red-600 text-white mb-4 px-4 py-2 text-sm font-semibold">
            FEATURED PROJECT
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {transformationProject.title}
          </h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-6">
            {transformationProject.description}
          </p>
          <div className="flex items-center justify-center gap-6 text-gray-400">
            <div className="flex items-center gap-2">
              <MapPin size={18} className="text-red-500" />
              <span>{transformationProject.location}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock size={18} className="text-red-500" />
              <span>Completed in {transformationProject.duration}</span>
            </div>
          </div>
        </div>

        {/* Transformation Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {transformationProject.phases.map((phase, index) => (
            <div key={phase.id} className="relative group">
              {/* Card */}
              <div className="relative overflow-hidden rounded-xl shadow-2xl">
                <div className="aspect-square overflow-hidden">
                  <img 
                    src={phase.image}
                    alt={phase.stage}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent">
                  <div className="absolute top-4 left-4">
                    <Badge 
                      className={`font-bold text-sm px-4 py-2 ${
                        phase.stage === 'BEFORE' ? 'bg-gray-600' :
                        phase.stage === 'DURING' ? 'bg-yellow-600' :
                        'bg-green-600'
                      } text-white`}
                    >
                      {phase.stage}
                    </Badge>
                  </div>
                  
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <p className="text-white text-sm leading-relaxed">
                      {phase.description}
                    </p>
                  </div>
                </div>

                {/* Step number */}
                <div className="absolute top-4 right-4 w-12 h-12 bg-red-600 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
                  {index + 1}
                </div>
              </div>

              {/* Arrow between cards (desktop only) */}
              {index < 2 && (
                <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-20">
                  <ArrowRight size={24} className="text-red-500" />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Features highlight */}
        <div className="bg-white/5 backdrop-blur-sm rounded-xl p-8 mb-8 border border-white/10">
          <h3 className="text-2xl font-bold text-white mb-6 text-center">
            What's Included in Our Service
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-start gap-3">
              <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={24} />
              <div>
                <h4 className="text-white font-semibold mb-1">Complete Removal</h4>
                <p className="text-gray-400 text-sm">Safe removal of old roof and proper disposal of materials</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={24} />
              <div>
                <h4 className="text-white font-semibold mb-1">Professional Installation</h4>
                <p className="text-gray-400 text-sm">Qualified team with years of roofing experience</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={24} />
              <div>
                <h4 className="text-white font-semibold mb-1">Quality Guarantee</h4>
                <p className="text-gray-400 text-sm">Premium materials with extended warranty</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <h3 className="text-2xl font-bold text-white mb-4">
            Ready to Transform Your Roof?
          </h3>
          <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
            Get a free estimate and discover how we can transform your roof with the same quality!
          </p>
          <Button 
            onClick={scrollToContact}
            size="lg"
            className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg font-semibold shadow-2xl"
          >
            Request Free Estimate
            <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default TransformationShowcase;
